import React, { useState, useCallback, useEffect } from 'react';
import { Tabs, Spin, message } from 'antd';
import './PWP253Form.css';

import { usePWP253Mode } from './hooks/usePWP253Mode';
import { usePWP253Record } from './hooks/usePWP253Record';
import { usePWP253Navigation } from './hooks/usePWP253Navigation';
import ActionBar from './components/ActionBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2ColorDetails from './components/Tab2ColorDetails';
import pwp253Api from '../../services/pwp253Api';
import type { FP630, FP631, FP632 } from '../../services/pwp253Api';

const { TabPane } = Tabs;

const EMPTY_MASTER: Partial<FP630> = {
  fp630_01: '',
  fp630_10: '',
  fp630_11: new Date().toISOString(),
  fp630_12: 1,
  fp630_13: '',
  fp630_14: '',
  fp630_15: 0,
  fp630_16: '',
  fp630_91: '',
  fp630_93: '',
};

const PWP253Form: React.FC = () => {
  const { mode, toView, toAdd, toEdit, isEditing } = usePWP253Mode();
  const { record, loading, load, save, remove, setRecord } = usePWP253Record();
  const { list, hasPrev, hasNext, fetchList, goFirst, goLast, goPrev, goNext, setCurrentById } =
    usePWP253Navigation();

  const [formData, setFormData] = useState<Partial<FP630>>(EMPTY_MASTER);
  const [details, setDetails] = useState<Partial<FP631>[]>([]);
  const [customerName, setCustomerName] = useState('');
  const [activeTab, setActiveTab] = useState('1');

  // Sync record to form + auto-resolve customer name
  useEffect(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
      // Resolve customer name on record load (BR-F19)
      if (record.fp630_14 && record.fp630_14.trim()) {
        pwp253Api.lookupCustomer(record.fp630_14.trim()).then(res => {
          setCustomerName(res.name || '');
        }).catch(() => setCustomerName(''));
      } else {
        setCustomerName('');
      }
    }
  }, [record]);

  const loadRecord = useCallback(async (id: string) => {
    const result = await load(id);
    if (result) {
      setCurrentById(id);
    }
    return result;
  }, [load, setCurrentById]);

  // Navigation handlers
  const handleFirst = useCallback(async () => {
    const id = goFirst();
    if (id) await loadRecord(id);
  }, [goFirst, loadRecord]);

  const handlePrev = useCallback(async () => {
    const id = goPrev();
    if (id) await loadRecord(id);
  }, [goPrev, loadRecord]);

  const handleNext = useCallback(async () => {
    const id = goNext();
    if (id) await loadRecord(id);
  }, [goNext, loadRecord]);

  const handleLast = useCallback(async () => {
    const id = goLast();
    if (id) await loadRecord(id);
  }, [goLast, loadRecord]);

  // Mode handlers
  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER, fp630_11: new Date().toISOString() });
    setDetails([]);
    setCustomerName('');
    setRecord(null);
    toAdd();
    setActiveTab('1');
  }, [toAdd, setRecord]);

  const handleEdit = useCallback(() => {
    if (!record) return;
    setFormData({ ...record });
    setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    toEdit();
  }, [record, toEdit]);

  const handleCancel = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    } else {
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
    }
    setCustomerName('');
    toView();
  }, [record, toView]);

  const handleSave = useCallback(async () => {
    const isNew = mode === 'add';
    const result = await save(formData, details, isNew);
    if (result) {
      toView();
      await fetchList();
      setCurrentById(result.fp630_01);
    }
  }, [mode, formData, details, save, toView, fetchList, setCurrentById]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    const ok = await remove(record.fp630_01);
    if (ok) {
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
      setCustomerName('');
      toView();
      await fetchList();
    }
  }, [record, remove, toView, fetchList]);

  // Field update
  const onChange = useCallback((field: keyof FP630, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Customer lookup — called when code is confirmed via blur or autocomplete select (BR-F19)
  const onCustomerCodeConfirm = useCallback(async (code: string) => {
    if (!code || !code.trim()) {
      setCustomerName('');
      return;
    }
    try {
      const result = await pwp253Api.lookupCustomer(code.trim());
      setCustomerName(result.name || '');
    } catch {
      setCustomerName('');
    }
  }, []);

  // Detail handlers
  const handleAddDetail = useCallback(() => {
    if (details.length >= 999) {
      message.warning('明細項數已達上限 999');
      return;
    }
    const prev = details.length > 0 ? details[details.length - 1] : null;
    const newDetail: Partial<FP631> = {
      fp631_02: details.length + 1,
      fp631_11: prev?.fp631_11 || '',
      fp631_12: prev?.fp631_12 || '',
      fp631_13: '',
      fp631_14: 0,
      fp631_15: 0,
      fp631_16: 0,
      fp631_17: 0,
      fp631_18: '',
      sub_details: [],
    };
    setDetails(prev2 => [...prev2, newDetail]);
    setFormData(prev2 => ({ ...prev2, fp630_15: details.length + 1 }));
  }, [details]);

  const handleDeleteDetail = useCallback((idx: number) => {
    setDetails(prev => {
      const updated = prev.filter((_, i) => i !== idx).map((d, i) => ({ ...d, fp631_02: i + 1 }));
      setFormData(p => ({ ...p, fp630_15: updated.length }));
      return updated;
    });
  }, []);

  const handleUpdateDetail = useCallback((idx: number, field: keyof FP631, value: any) => {
    setDetails(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  }, []);

  const handleAddSubDetail = useCallback((detailIdx: number) => {
    setDetails(prev => prev.map((d, i): Partial<FP631> => {
      if (i !== detailIdx) return d;
      const subs = d.sub_details || [];
      if (subs.length >= 999) {
        message.warning('顏色子明細項數已達上限 999');
        return d;
      }
      const newSub: Partial<FP632> = {
        fp632_02: i + 1,
        fp632_03: subs.length + 1,
        fp632_12: 0,
        fp632_13: 0,
        fp632_14: '',
      };
      return { ...d, sub_details: [...subs, newSub], fp631_17: subs.length + 1 };
    }));
  }, []);

  const handleDeleteSubDetail = useCallback((detailIdx: number, subIdx: number) => {
    setDetails(prev => prev.map((d, i): Partial<FP631> => {
      if (i !== detailIdx) return d;
      const subs = (d.sub_details || []).filter((_, si) => si !== subIdx)
        .map((s, si) => ({ ...s, fp632_03: si + 1 }));
      return { ...d, sub_details: subs, fp631_17: subs.length };
    }));
  }, []);

  const handleUpdateSubDetail = useCallback((detailIdx: number, subIdx: number, field: keyof FP632, value: any) => {
    setDetails(prev => prev.map((d, i): Partial<FP631> => {
      if (i !== detailIdx) return d;
      const subs = (d.sub_details || []).map((s, si) => si === subIdx ? { ...s, [field]: value } : s);
      return { ...d, sub_details: subs };
    }));
  }, []);

  // Auto-load first record
  useEffect(() => {
    if (list.length > 0 && !record && mode === 'view') {
      loadRecord(list[0].fp630_01);
    }
  }, [list]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="pwp253-form">
      <div className="page-wrapper">
        <Spin spinning={loading}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 16 }}>P253-送貨明細</span>
            {record && (
              <span style={{ marginLeft: 16, color: '#666' }}>
                單號：{record.fp630_01}
              </span>
            )}
          </div>

          <ActionBar
            mode={mode}
            hasRecord={!!record}
            hasPrev={hasPrev}
            hasNext={hasNext}
            loading={loading}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            onDelete={handleDelete}
            onFirst={handleFirst}
            onPrev={handlePrev}
            onNext={handleNext}
            onLast={handleLast}
          />

          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="基本資料" key="1">
              <Tab1BasicInfo
                formData={formData}
                isEditing={isEditing}
                customerName={customerName}
                onChange={onChange}
                onCustomerCodeConfirm={onCustomerCodeConfirm}
              />
            </TabPane>

            <TabPane tab="COLOR明細" key="2">
              <Tab2ColorDetails
                details={details}
                totalItems={formData.fp630_15 || 0}
                isEditing={isEditing}
                onAddDetail={handleAddDetail}
                onDeleteDetail={handleDeleteDetail}
                onUpdateDetail={handleUpdateDetail}
                onAddSubDetail={handleAddSubDetail}
                onDeleteSubDetail={handleDeleteSubDetail}
                onUpdateSubDetail={handleUpdateSubDetail}
              />
            </TabPane>
          </Tabs>
        </Spin>
      </div>
    </div>
  );
};

export default PWP253Form;
